/**
 * Rendering an MCP call's arguments for the tool-call row and its hover panel.
 *
 * An MCP server defines its own argument shape, so unlike the native tools
 * there is no field the chat can single out — the whole payload is what a
 * reader has to go on. That makes this the one place in a transcript that
 * prints an arbitrary, server-authored object verbatim, which is why the
 * formatting here is defensive: secrets are masked, the output is capped, and
 * a payload that can't be serialized says so rather than rendering as an empty
 * object that reads like "this call took no arguments".
 */

/** Shown in place of a value whose key names it as a credential. */
export const REDACTED = '[redacted]';

/**
 * Stands in for the whole payload when it can't be serialized (a circular
 * structure from a buggy server, a `toJSON` that throws). Phrased as a
 * sentence rather than `{}`, which a reader would take at face value as an
 * argument-less call.
 */
export const MCP_ARGS_UNAVAILABLE = 'Arguments unavailable — they could not be read.';

/**
 * Words that name a credential on their own, in whatever casing or separator
 * style the server chose.
 */
const SECRET_WORDS = new Set([
  'token',
  'secret',
  'secrets',
  'password',
  'passwd',
  'credential',
  'credentials',
  'authorization',
  'apikey',
]);

/** Words that make a following `key` a credential rather than a map key. */
const KEY_QUALIFIERS = new Set(['api', 'access', 'secret', 'private', 'signing']);

/**
 * Split a key into lowercase words, treating camelCase humps and any
 * non-alphanumeric run as boundaries: `apiKey`, `api_key`, and `X-API-Key` all
 * come out as `['api', 'key']`.
 */
function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
}

/**
 * Whether a key's value is a credential rather than something a reader needs.
 * The transcript is shared (linked, screenshotted, pasted into a review), and
 * an MCP server is free to take an API token as a plain argument, so a value
 * the row would otherwise print in full is masked on the way out.
 *
 * Matched on whole words, so `authToken` and `api-key` are caught while
 * `secretary` and `passwordless` are left alone. A bare `key` is not a
 * credential — it is as often the key half of a key/value pair — so it only
 * counts alongside a qualifier that makes it one.
 */
function isSecretKey(key: string): boolean {
  const words = keyWords(key);
  return words.some(
    (word, i) =>
      SECRET_WORDS.has(word) || (word === 'key' && i > 0 && KEY_QUALIFIERS.has(words[i - 1])),
  );
}

/**
 * Past this many characters the panel is a wall of JSON nobody reads, and the
 * cost is real: an MCP result can embed a whole file, and the surrounding turn
 * re-renders every row on every streamed token. The same reasoning as
 * {@link capReadLines} for shell reads, and the same resolution — show the
 * head, say what was left out.
 */
const MAX_ARGS_CHARS = 10_000;

/**
 * Replace credential-looking values with {@link REDACTED}, recursively.
 *
 * Only the *value* is replaced, never the key, so the row still shows that the
 * call took a token — just not which one. Objects already visited are left
 * alone so a self-referential payload terminates here rather than in
 * `JSON.stringify`.
 */
function redactSecrets(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (Array.isArray(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    return value.map((item) => redactSecrets(item, seen));
  }
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      // A nested object under a secret-ish key (`credentials: { … }`) is
      // masked whole: every leaf under it is the credential.
      isSecretKey(key) ? REDACTED : redactSecrets(item, seen),
    ]),
  );
}

/**
 * `JSON.stringify` that never throws, so a malformed MCP payload degrades to
 * `undefined` instead of crashing the row.
 */
function safeJsonStringify(value: unknown, indent?: number): string | undefined {
  try {
    return JSON.stringify(value, null, indent);
  } catch {
    return undefined;
  }
}

/**
 * The text an MCP call's arguments render as: redacted, capped, and never
 * silently empty. Pass `indent` for the multi-line hover panel and omit it for
 * the single-line row.
 */
export function formatMcpArgs(input: Record<string, unknown> | null, indent?: number): string {
  const json = safeJsonStringify(redactSecrets(input ?? {}), indent);
  if (json === undefined) return MCP_ARGS_UNAVAILABLE;
  if (json.length <= MAX_ARGS_CHARS) return json;
  const dropped = json.length - MAX_ARGS_CHARS;
  return `${json.slice(0, MAX_ARGS_CHARS)}\n… ${dropped.toLocaleString()} more characters`;
}
